import { isValidLocalPart } from "@catmail/shared";
import { useEffect, useState } from "react";

import { FormDialog, TextField } from "@/components";

/**
 * 신청된 주소를 **고쳐서** 받아주는 창. 최고 관리자만 본다.
 *
 * 예전에는 받거나 거절뿐이었다. 그래서 `nelflix@`처럼 오타 하나가 섞여 오면
 * 거절할 수밖에 없었는데, 거절당한 사람 눈에는 이유가 안 보인다 — 같은 오타로
 * 다시 신청하고 관리자는 같은 일을 또 한다. 문지기가 사람인 이상 고쳐서
 * 받아주는 길이 있어야 한다.
 *
 * 이미 받아준 주소도 고칠 수 있다. 방과 대화는 주소가 아니라 identity에
 * 매달려 있어서 그대로 따라온다.
 */

type EditAddressDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 지금 주소. 앞부분을 미리 채워둔다 — 대개 한두 글자만 고친다 */
  address: string;
  /** `@` 뒤에 붙는 것. 하나뿐이라 고르지 않고 보여주기만 한다 */
  mailDomain: string;
  /**
   * 이미 받아준 주소인가.
   *
   * 기다리는 중인 것을 고치는 것과 무게가 다르다. 승인 전에는 그 주소로 오는
   * 메일이 어차피 거절되므로 아직 아무도 그 주소를 쓰지 않았다.
   */
  approved?: boolean;
  /** 로그인에 쓰는 대표 주소인가. 고치면 **로그인 아이디가 바뀐다** */
  isPrimary?: boolean;
  pending?: boolean;
  /** 서버가 거절한 이유. "이미 쓰이고 있는 주소입니다" 같은 것 */
  error?: string | null;
  onSubmit: (localPart: string) => void;
};

export function EditAddressDialog({
  open,
  onOpenChange,
  address,
  mailDomain,
  approved = false,
  isPrimary = false,
  pending = false,
  error,
  onSubmit,
}: EditAddressDialogProps) {
  const current = address.split("@")[0] ?? "";
  const [localPart, setLocalPart] = useState(current);

  // 다른 줄의 고치기를 눌렀을 때 앞사람의 주소가 남아 있으면 안 된다.
  useEffect(() => {
    if (open) setLocalPart(current);
  }, [open, current]);

  const trimmed = localPart.trim().toLowerCase();
  const malformed = trimmed.length > 0 && !isValidLocalPart(trimmed);
  const unchanged = trimmed === current;

  const hint = malformed
    ? "영문 소문자와 숫자로 시작하고, . _ - 만 쓸 수 있어요"
    : (error ?? null);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="주소 고치기"
      description={`지금은 ${address} 예요.`}
      submitLabel="고치기"
      pendingLabel="고치는 중…"
      pending={pending}
      // 같은 주소로 확인을 누르면 서버가 400을 준다. 누르기 전에 막는 편이 낫다.
      canSubmit={isValidLocalPart(trimmed) && !unchanged}
      hint={
        hint ? (
          <p className="text-danger text-[13px]">{hint}</p>
        ) : approved ? (
          /*
            이미 열린 주소를 고치는 것은 **바깥이 모르는 변경**이다. 옛 주소를
            아는 사람이 보낸 메일은 이 순간부터 엣지에서 거절된다. 대표 주소면
            로그인 아이디까지 바뀌므로 그것부터 말한다.
          */
          <p className="text-danger text-[13px]">
            {isPrimary
              ? "로그인에 쓰는 주소예요. 고치면 새 주소로 로그인해야 하고, 옛 주소로 온 메일은 들어오지 않아요."
              : "이미 열린 주소예요. 고치면 옛 주소로 온 메일은 들어오지 않아요."}
          </p>
        ) : (
          <p className="text-muted-3 text-[13px]">
            아직 열리지 않은 주소라 지금 고쳐도 오던 메일이 끊기지 않아요.
          </p>
        )
      }
      onSubmit={() => onSubmit(trimmed)}
    >
      <TextField
        label="주소"
        autoFocus
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        trailing={<span className="text-muted-2 text-[15px]">@{mailDomain}</span>}
        value={localPart}
        onChange={(event) => setLocalPart(event.target.value)}
      />
    </FormDialog>
  );
}
